/* @refresh reload */
import { X, FileText, Mic, Network, ChevronLeft, ChevronRight, Folder, Inbox, LayoutDashboard, House, MessageSquare, Pin, Replace, User, Activity } from "lucide-react"
import { CreateInFolderDropdown } from "@/components/ui/create-in-folder-menu"
import { BasecampIcon } from "@/components/icons/basecamp-icon"
import { GoogleDriveIcon } from "@/components/icons/google-drive-icon"
import { NextcloudIcon } from "@/components/icons/nextcloud-icon"
import { getDocumentDisplayIcon } from "@/lib/document-icons"
import { cn } from "@/lib/utils"
import { SHELL_COLUMN_HEADER_CLASS } from "@/lib/shell-chrome"
import { type CreateInFolderActionId } from "@/lib/create-in-folder"
import { displayFolderTitleForStoragePath, type WorkspaceRouteInfo } from '@/lib/app-routes'
import {
  parseSidebarDocumentDragJson,
  type SidebarDocumentDragPayload,
} from "@/lib/sidebar-document-drag"
import { useWorkspace, type Workspace } from "@/providers/workspace-provider"
import {
  useWorkspaceRowContextMenu,
  WorkspaceRowContextMenuPortal,
} from "@/components/layout/left-sidebar.workspace-row-menu"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useState, useEffect, useRef, useMemo, useCallback, memo, type CSSProperties, type MouseEvent, type ReactNode } from "react"
import {
  TabContextMenuHost,
  type TabMenuHandlers,
} from "@/components/features/tab-context-menu-host"
import {
  isHomeTab,
  isTabVisibleForActiveWorkspaces,
  pinHomeTabFirstInOpenTabs,
  resolveTabWorkspaceId,
  sortAppGroupTabs,
} from "@/components/layout/resizable-layout.handlers/shared"
import {
  tabGroupCollapseStripClass,
  tabGroupEnterClass,
  tabGroupExitClass,
  tabGroupMotionDurationMs,
} from "@/lib/tab-group-motion"
import { useTabGroupStripWidth } from "@/hooks/use-tab-group-strip-width"
import { testIds } from "@/lib/test-ids"
import type { BasecampAssignment, BasecampProject } from "@/types/basecamp"

/** Maps a tab to extension context for unified ContextMenu (avoids hardcoded basecamp- prefixes in the menu component). */
export function getTabExtensionContext(tab: OpenTab): {
  extensionId?: string
  extensionEntityType?: string
  extensionEntity?: unknown
} {
  if (tab.id.startsWith("basecamp-todo-")) {
    const todoId = tab.id.replace("basecamp-todo-", "")
    const projectId = tab.integrationData?.projectId ?? ""
    const entity: BasecampAssignment = {
      id: todoId,
      title: tab.title,
      url: tab.integrationData?.todoData?.url ?? "",
      _app_url: tab.integrationData?.todoData?._app_url,
      ...(tab.integrationData?.todoData ?? {}),
      project: {
        id: projectId,
        name: tab.integrationData?.projectName ?? tab.integrationData?.todoData?.project?.name ?? "",
      },
      assignee: { id: "", name: "" },
      completed: false,
    }
    return {
      extensionId: "basecamp",
      extensionEntityType: "todo",
      extensionEntity: entity,
    }
  }
  if (tab.id.startsWith("basecamp-project-")) {
    const projectId = tab.id.replace("basecamp-project-", "")
    const entity: BasecampProject & { organizationId?: string } = {
      id: projectId,
      name: tab.title,
      url: "",
      app_url: "",
      dock: [],
      organizationId: tab.integrationData?.organizationId,
    }
    return {
      extensionId: "basecamp",
      extensionEntityType: "project",
      extensionEntity: entity,
    }
  }
  return {}
}

/** Stored document tabs (extensionless URL routes). */
export type DocumentTabType =
  | "work-spec"
  | "note"
  | "voice-memo"
  | "flow"
  | "todo"
  | "meeting"
  | "idea"
  | "research"
  | "skill"
  | "quiz"

export type OpenTab = {
  id: string
  title: string
  type:
    | DocumentTabType
    | "explorer"
    | "home"
    | "inbox"
    | "inbox-thread"
    | "chat-conversation"
    | "graph-view"
    | "workspace-home"
    | "desk-home"
    | "integration-basecamp"
    | "integration-basecamp-todo"
    | "integration-basecamp-todo-list"
    | "integration-basecamp-triage"
    | "integration-voice-memos"
    | "integration-product-board"
    | "integration-knowledge-hub"
    | "integration-google-drive"
    | "integration-google-drive-preview"
    | "integration-nextcloud"
    | "platform-skill"
    | "search-view"
    | "person-view"
    | "flow-index"
    | "edit-history"
  isDirty?: boolean
  isPinned?: boolean
  tabSlotId?: string
  workspaceId?: string | null
  filePath?: string | null
  /** When type is desk-home — member desk slug under users/ */
  deskSlug?: string | null
  initialQuery?: string
  /** Search host named view - drives /search?view= (e.g. activity). */
  searchNamedView?: 'search' | 'activity'
  /** Path scope for Advanced Search (/search?path=). */
  searchPath?: string
  /** Activity member filter (`/search?view=activity&user=`). */
  searchUpdatedBy?: string
  /** When type is person-view - FlowNotes user UUID */
  personUserId?: string
  /** Search meaning-search mode (`/search?semantic=1`). */
  searchSemantic?: boolean
  explorerData?: {
    folderPath: string | null
    /** Browse Cmd+F query. Emitted as `?q=` only when open/nonempty. */
    panelSearchQuery?: string
  }
  /** When type is inbox-thread — DB thread UUID */
  inboxThreadId?: string
  /** When type is chat-conversation — agent chat thread UUID */
  chatThreadId?: string
  integrationData?: {
    integrationId: string
    projectId?: string
    todoId?: string
    listId?: string
    listName?: string
    projectName?: string
    section?: string
    organizationId?: string
    /** Workspace slug from deep link URL (/{slug}/basecamp/...); used before org list hydrates. */
    workspaceSlug?: string
    todoData?: any
    /** BC5 subtask to highlight when viewing parent todo */
    highlightStepId?: string
    /** Knowledge Hub queue filter */
    statusFilter?: 'review-requested' | 'needs-review' | null
    /** Serializable breadcrumb trail for Basecamp todo list / todo tabs */
    breadcrumbTrail?: Array<{
      id: string
      title: string
      type: 'projects' | 'project' | 'todoset' | 'todolist' | 'section'
    }>
    /** Google Drive preview tab */
    driveFileId?: string
    driveMimeType?: string
    driveWebViewLink?: string
    importedDocumentId?: string
    driveIsShortcut?: boolean
    driveShortcutSourceId?: string
    nextcloudPath?: string
    nextcloudKind?: 'file' | 'folder'
    nextcloudFileId?: string
    nextcloudPreviewPath?: string
    nextcloudSurface?: 'browse' | 'document'
  }
}

// Backward compatibility (can remove later)
export type OpenDocument = OpenTab

export const getTabIcon = (type: OpenTab["type"]) => {
  switch (type) {
    case "work-spec":
    case "note":
    case "voice-memo":
    case "flow":
    case "todo":
    case "meeting":
    case "idea":
    case "research":
    case "skill":
    case "platform-skill":
    case "quiz":
      return getDocumentDisplayIcon({ type, kind: type }, { className: "h-3 w-3" })
    case "explorer":
      return <Folder className="h-3 w-3 shrink-0" />
    case "home":
      return <House className="h-3 w-3 text-muted-foreground" />
    case "integration-basecamp":
      return <BasecampIcon className="h-3 w-3.5" />
    case "integration-basecamp-todo":
      return <BasecampIcon className="h-3 w-3.5" />
    case "integration-basecamp-todo-list":
      return <BasecampIcon className="h-3 w-3.5" />
    case "integration-basecamp-triage":
      return <BasecampIcon className="h-3 w-3.5" />
    case "integration-voice-memos":
      return <Mic className="h-3 w-3 text-purple-500" />
    case "integration-google-drive":
    case "integration-google-drive-preview":
      return <GoogleDriveIcon className="h-3 w-3 text-blue-500" />
    case "integration-nextcloud":
      return <NextcloudIcon className="h-3 w-3" />
    case "graph-view":
      return <Network className="h-3 w-3 text-muted-foreground" />
    case "workspace-home":
      return <LayoutDashboard className="h-3 w-3 text-primary" />
    case "desk-home":
      return <User className="h-3 w-3 text-primary" />
    case "inbox":
    case "inbox-thread":
      return <Inbox className="h-3 w-3 text-sky-500" />
    case "chat-conversation":
      return <MessageSquare className="h-3 w-3 text-violet-500" />
    case "edit-history":
      return <FileText className="h-3 w-3 text-muted-foreground" />
    case "person-view":
      return <User className="h-3 w-3 text-muted-foreground" />
    case "flow-index":
      return <Activity className="h-3 w-3 text-muted-foreground" />
    default:
      return <FileText className="h-3 w-3 text-muted-foreground" />
  }
}

export const getExplorerTabTitle = (
  tab: OpenTab,
  workspaces: WorkspaceRouteInfo[] = []
): string => {
  if (tab.type !== "explorer") return tab.title

  const folderPath = tab.explorerData?.folderPath
  if (!folderPath) {
    return "Explorer"
  }

  if (workspaces.length > 0) {
    return displayFolderTitleForStoragePath(folderPath, workspaces)
  }

  const parts = folderPath.split('/')
  const folderName = parts[parts.length - 1]

  return folderName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** Leftmost tab group: Home, Edit History, Inbox, etc. (not workspace-scoped). */
export const APP_TAB_GROUP_KEY = '__ungrouped__'
const UNGROUPED_KEY = APP_TAB_GROUP_KEY

interface TabGroupHeaderProps {
  groupId: string
  label: string
  tabCount: number
  isCollapsed: boolean
  onToggleCollapse: () => void
  onContextMenu: (event: MouseEvent) => void
}

function getInitials(name: string): string {
  return name
    .split(/[\s-]+/)
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 3)
}

function TabGroupStrip({
  isCollapsed,
  tabCount,
  children,
}: {
  isCollapsed: boolean
  tabCount: number
  children: ReactNode
}) {
  const [stripRef, stripWidthPx] = useTabGroupStripWidth([tabCount, isCollapsed])
  return (
    <div
      ref={stripRef}
      className={cn(
        'tab-group__strip flex items-end gap-0.5',
        tabGroupCollapseStripClass(isCollapsed),
      )}
      style={
        stripWidthPx != null
          ? ({ '--tab-group-strip-w': `${stripWidthPx}px` } as CSSProperties)
          : undefined
      }
    >
      {children}
    </div>
  )
}

function TabGroupHeader({ groupId, label, tabCount, isCollapsed, onToggleCollapse, onContextMenu }: TabGroupHeaderProps) {
  const initials = getInitials(label)
  return (
    <button
      type="button"
      onClick={onToggleCollapse}
      onContextMenu={onContextMenu}
      data-testid={testIds.documentTabs.groupHeader(groupId)}
      className="tab-group__header flex items-center gap-0 px-0 py-0 mb-px border-none cursor-pointer bg-transparent shrink-0"
      title={isCollapsed ? `${label} (${tabCount} tabs) - click to expand` : `${label} - click to collapse`}
    >
      <span
        className="tab-group__badge inline-flex items-center justify-center px-1.5 py-0.5 rounded-t text-[10px] font-bold leading-none tracking-wide"
        style={{ minWidth: '1.5rem' }}
      >
        {isCollapsed ? `${initials} ${tabCount}` : initials}
      </span>
    </button>
  )
}

export interface DocumentTabsProps {
  openTabs: OpenTab[]
  activeTabId: string | null
  onTabClick: (tabId: string) => void
  onTabClose: (tabId: string) => void
  onTabReorder?: (reorderedTabs: OpenTab[]) => void
  onTabDragToChat?: (tabId: string, title: string, type: OpenTab["type"]) => void
  /** HTML5 drop from sidebar explorer / tree (`application/json` with path + nodeType) */
  onNativeSidebarItemDrop?: (payload: SidebarDocumentDragPayload) => void
  onTabPinToggle?: (tabId: string) => void
  onTabShowFileLocation?: (tabId: string) => void
  onTabRename?: (tabId: string) => void
  onTabMove?: (tabId: string) => void
  onTabExportPDF?: (tabId: string) => void
  onTabAddToChat?: (tabId: string) => void
  onTabCloseOthers?: (tabId: string) => void
  onTabMoveToWindow?: (tabId: string) => void
  onTabCopyToWindow?: (tabId: string) => void
  getTabFilePath?: (tabId: string) => Promise<string | null>
  onTabToggleBookmark?: (tabId: string) => Promise<void>
  getTabBookmarkStatus?: (tabId: string) => Promise<{ isBookmarked: boolean | null; isToggling: boolean }>
  /** Create Untitled Note in the default folder (sibling / browse / desk). */
  onNewTab?: () => void
  /** Import .md / .txt into the default folder (tab-bar flyout). */
  onImportInFolder?: () => void
  /** Create a folder inside the default folder (tab-bar flyout). */
  onNewFolder?: () => void
  /** Open folder picker, then create in the chosen folder. */
  onChooseNewDocumentFolder?: () => void
  /** Short folder name for "New document in {label}" menu copy. */
  newDocumentFolderLabel?: string | null
  /**
   * Local text-import drop affordance on the tab bar.
   * `placement`: insert left of active, replace active, or insert right of active.
   */
  ghostImportTab?: {
    folderPath: string
    label?: string
    placement?: 'left' | 'right' | 'replace'
    relativeToTabId?: string | null
  } | null
}

function GhostImportTabChrome({
  label,
  placement,
}: {
  label: string
  placement: 'left' | 'right' | 'replace'
}) {
  if (placement === 'replace') {
    return (
      <div
        className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center gap-1 rounded-t-md bg-primary/25 ring-2 ring-inset ring-primary"
        data-testid={testIds.documentViewer.textImportGhostTab}
        aria-hidden
      >
        <Replace className="h-3.5 w-3.5 text-primary" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
          Replace
        </span>
      </div>
    )
  }

  return (
    <div
      className="flex items-center gap-1 shrink-0 mb-0.5"
      data-testid={testIds.documentViewer.textImportGhostTab}
      aria-hidden
    >
      {placement === 'left' ? (
        <ChevronLeft className="h-3.5 w-3.5 text-primary shrink-0" />
      ) : null}
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-t-md bg-primary/20 text-primary ring-1 ring-inset ring-primary/80">
        <FileText className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate text-xs font-medium max-w-[140px]">{label}</span>
      </div>
      {placement === 'right' ? (
        <ChevronRight className="h-3.5 w-3.5 text-primary shrink-0" />
      ) : null}
    </div>
  )
}

// Memoized individual sortable tab component to prevent re-render cascades
const SortableTab = memo(function SortableTab({
  tab,
  isActive,
  onTabClick,
  onTabClose,
  onTabPinToggle,
  totalTabs,
  onTabShowFileLocation,
  onTabRename,
  onTabMove,
  onTabExportPDF,
  onTabAddToChat,
  onTabCloseOthers,
  onTabMoveToWindow,
  onTabCopyToWindow,
  getTabFilePath,
  onTabToggleBookmark,
  getTabBookmarkStatus,
  workspacesRoute = [],
  workspaceGroupId,
  groupCollapsed = false,
  onTabToggleGroupCollapsed,
}: {
  tab: OpenTab
  isActive: boolean
  onTabClick: (tabId: string) => void
  onTabClose: (tabId: string) => void
  onTabPinToggle?: (tabId: string) => void
  totalTabs: number
  onTabShowFileLocation?: (tabId: string) => void
  onTabRename?: (tabId: string) => void
  onTabMove?: (tabId: string) => void
  onTabExportPDF?: (tabId: string) => void
  onTabAddToChat?: (tabId: string) => void
  onTabCloseOthers?: (tabId: string) => void
  onTabMoveToWindow?: (tabId: string) => void
  onTabCopyToWindow?: (tabId: string) => void
  getTabFilePath?: (tabId: string) => Promise<string | null>
  onTabToggleBookmark?: (tabId: string) => Promise<void>
  getTabBookmarkStatus?: (tabId: string) => Promise<{ isBookmarked: boolean | null; isToggling: boolean }>
  workspacesRoute?: WorkspaceRouteInfo[]
  workspaceGroupId?: string
  groupCollapsed?: boolean
  onTabToggleGroupCollapsed?: (workspaceGroupId: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled: isHomeTab(tab) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const getTabWidth = () => {
    if (totalTabs > 6) {
      return "min-w-[80px] max-w-[140px]"
    } else if (totalTabs > 4) {
      return "min-w-[90px] max-w-[160px]"
    } else {
      return "min-w-[100px] max-w-[180px]"
    }
  }

  const tabWidthClass = getTabWidth()

  const renderTabContent = () => {
    const icon = getTabIcon(tab.type)
    const dirtyIndicator = tab.isDirty && <span className="text-primary ml-1">•</span>

    const displayTitle = tab.type === "explorer" ? getExplorerTabTitle(tab, workspacesRoute) : tab.title
    const isUnpinned = !tab.isPinned
    return (
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {tab.isPinned ? (
          <>
            <Pin
              className="h-3 w-3 shrink-0 text-muted-foreground"
              aria-label="Pinned tab"
              data-testid={testIds.documentTabs.pinIcon}
            />
            <span
              className="tab-group__pin-icon flex items-center justify-center w-4 h-4 rounded shrink-0"
              style={{ color: 'var(--ws-color, currentColor)' }}
            >
              {icon}
            </span>
          </>
        ) : (
          <span
            className={cn(
              'shrink-0 flex items-center',
              isUnpinned && 'text-muted-foreground/80',
            )}
          >
            {icon}
          </span>
        )}
        <span className={cn(
          "text-xs truncate flex-1 min-w-0",
          isUnpinned && "italic text-muted-foreground/80"
        )}>
          {displayTitle}
          {dirtyIndicator}
        </span>
      </div>
    )
  }

  // Track drag state with ref (no re-renders)
  const isDraggingRef = useRef(false)
  const dragStartPos = useRef<{ x: number; y: number } | null>(null)

  const isExplorerTab = tab.type === 'explorer'

  const menuHandlers: TabMenuHandlers = {
    onTabToggleGroupCollapsed,
    onTabPinToggle,
    onTabClose,
    onTabCloseOthers,
    onTabShowFileLocation,
    onTabRename,
    onTabMove,
    onTabExportPDF,
    onTabAddToChat,
    onTabMoveToWindow,
    onTabCopyToWindow,
    getTabFilePath,
    onTabToggleBookmark,
    getTabBookmarkStatus,
  }

  /** dnd-kit reorder: whole tab for Explorer; grip-only for document tabs so HTML5 drag to chat works on the title row */
  const customListeners = {
    ...listeners,
    onPointerDown: (e: React.PointerEvent) => {
      const target = e.target as HTMLElement

      if (e.button === 2) {
        return
      }

      const isCloseButton = target.closest('button[data-close-tab]')
      dragStartPos.current = { x: e.clientX, y: e.clientY }

      if (isCloseButton) {
        return
      }

      if (listeners?.onPointerDown) {
        isDraggingRef.current = true
        listeners.onPointerDown(e as any)
      }
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (listeners?.onPointerMove) {
        listeners.onPointerMove(e as any)
      }
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (dragStartPos.current) {
        const deltaX = Math.abs(e.clientX - dragStartPos.current.x)
        const deltaY = Math.abs(e.clientY - dragStartPos.current.y)
        const wasDrag = deltaX > 8 || deltaY > 8

        if (!wasDrag) {
          isDraggingRef.current = false
        }
      }

      dragStartPos.current = null

      if (listeners?.onPointerUp) {
        listeners.onPointerUp(e as any)
      }
    }
  }

  const tabTitleButton = (
    <button
      type="button"
      data-testid={testIds.documentViewer.tabPin(tab.id)}
      onClick={(e) => {
        if (isDragging || isDraggingRef.current) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
        onTabClick(tab.id)
      }}
      onDoubleClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        if (onTabPinToggle) {
          onTabPinToggle(tab.id)
        }
      }}
      className="flex items-center min-w-0 gap-2 flex-1 pl-1 pointer-events-auto text-left"
      title={isExplorerTab
        ? getExplorerTabTitle(tab, workspacesRoute)
        : `${tab.title}${!tab.isPinned ? " (Double-click to pin)" : ""}`}
    >
      {renderTabContent()}
    </button>
  )

  return (
    <TabContextMenuHost
      tab={tab}
      totalTabs={totalTabs}
      workspacesRoute={workspacesRoute}
      workspaceGroupId={workspaceGroupId}
      groupCollapsed={groupCollapsed}
      handlers={menuHandlers}
    >
      {({ onContextMenu }) => (
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...customListeners}
          onContextMenu={onContextMenu}
          data-testid={testIds.documentViewer.tab(tab.id)}
          className={cn(
            "tab-group__tab group flex items-center gap-1 px-2 py-1.5 min-w-0 flex-shrink-0 relative rounded-t-lg",
            tabWidthClass,
            isActive && "tab-group__tab--active text-[hsl(var(--tab-active-text))]",
            !isActive && "text-[hsl(var(--tab-inactive-text))]",
            isDragging && "opacity-50 z-50",
            !isHomeTab(tab) && "cursor-grab active:cursor-grabbing"
          )}
        >
          {tabTitleButton}

          {!(isHomeTab(tab) && totalTabs <= 1) && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onTabClose(tab.id)
              }}
              onPointerDown={(e) => {
                e.stopPropagation()
              }}
              data-close-tab
              data-testid={testIds.documentViewer.tabClose(tab.id)}
              className="opacity-0 group-hover:opacity-40 hover:!opacity-100 rounded p-0.5 transition-opacity relative z-20"
              title="Close tab"
              style={{ pointerEvents: 'auto' }}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </TabContextMenuHost>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.tab === nextProps.tab &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.totalTabs === nextProps.totalTabs &&
    prevProps.onTabClick === nextProps.onTabClick &&
    prevProps.onTabClose === nextProps.onTabClose &&
    prevProps.onTabPinToggle === nextProps.onTabPinToggle &&
    prevProps.onTabAddToChat === nextProps.onTabAddToChat
  )
})

export function DocumentTabs({
  openTabs = [],
  activeTabId,
  onTabClick,
  onTabClose,
  onTabReorder,
  onTabDragToChat,
  onNativeSidebarItemDrop,
  onTabPinToggle,
  onTabShowFileLocation,
  onTabRename,
  onTabMove,
  onTabExportPDF,
  onTabAddToChat,
  onTabCloseOthers,
  onTabMoveToWindow,
  onTabCopyToWindow,
  getTabFilePath,
  onTabToggleBookmark,
  getTabBookmarkStatus,
  onNewTab,
  onImportInFolder,
  onNewFolder,
  onChooseNewDocumentFolder,
  newDocumentFolderLabel = null,
  ghostImportTab = null,
}: DocumentTabsProps) {
  const { workspaces, activeWorkspaceIds, toggleWorkspace, hideOtherWorkspaces } = useWorkspace()
  const {
    menu: wsRowMenu,
    menuRef: wsRowMenuRef,
    openMenu: openWsRowMenu,
    closeMenu: closeWsRowMenu,
  } = useWorkspaceRowContextMenu()
  /** One workspace selected - tint the full tab bar with that workspace color. */
  const workspaceFocus = activeWorkspaceIds.length === 1
  const focusWorkspaceColor = workspaceFocus
    ? (workspaces.find((w) => w.id === activeWorkspaceIds[0])?.color ?? null)
    : null
  const workspacesRoute = useMemo(
    () =>
      workspaces.map((w) => ({
        id: w.id,
        slug: w.slug,
        name: w.name,
        contentRoot: w.contentRoot,
      })),
    [workspaces],
  )
  const [activeId, setActiveId] = useState<string | null>(null)

  const visibleTabs = useMemo(
    () =>
      openTabs.filter((tab) =>
        isTabVisibleForActiveWorkspaces(tab, workspaces, activeWorkspaceIds, activeTabId),
      ),
    [openTabs, workspaces, activeWorkspaceIds, activeTabId],
  )
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Persisted group display order — allows inbox/ungrouped tabs to be positioned
  // before, between, or after workspace groups. Workspace tabs can only reorder
  // within their own group; the ungrouped (inbox) tab group can be placed anywhere.
  const [groupOrder, setGroupOrder] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('flownotes_tab_group_order')
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  const persistGroupOrder = useCallback((order: string[]) => {
    setGroupOrder(order)
    localStorage.setItem('flownotes_tab_group_order', JSON.stringify(order))
  }, [])

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('flownotes_collapsed_tab_groups')
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  })

  useEffect(() => {
    localStorage.setItem('flownotes_collapsed_tab_groups', JSON.stringify([...collapsedGroups]))
  }, [collapsedGroups])

  const toggleGroupCollapsed = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }, [])

  type TabGroup = { groupId: string; workspace: Workspace | null; tabs: OpenTab[] }
  const groups = useMemo(() => {
    const byId = new Map<string, TabGroup>()
    const sidebarWorkspaceList = workspaces.filter((w) => w.showInSidebar)
    for (const w of sidebarWorkspaceList) {
      byId.set(w.id, { groupId: w.id, workspace: w, tabs: [] })
    }
    byId.set(UNGROUPED_KEY, { groupId: UNGROUPED_KEY, workspace: null, tabs: [] })

    // Resolve integration tabs to their parent workspace when tab.workspaceId is missing.
    // Prefer a 'work' workspace that is actually enabled in the switcher (activeWorkspaceIds),
    // not merely the first 'work' row in sidebar order (which could be a disabled org).
    const defaultWorkWs =
      sidebarWorkspaceList.find(
        (w) => w.type === 'work' && activeWorkspaceIds.includes(w.id)
      ) ?? sidebarWorkspaceList.find((w) => w.type === 'work')

    const seenIds = new Set<string>()
    for (const tab of visibleTabs) {
      if (seenIds.has(tab.id)) continue
      seenIds.add(tab.id)
      let id = isHomeTab(tab)
        ? UNGROUPED_KEY
        : resolveTabWorkspaceId(tab, workspaces) ?? UNGROUPED_KEY
      if (id === UNGROUPED_KEY && tab.type?.startsWith('integration-') && defaultWorkWs) {
        id = defaultWorkWs.id
      }
      if (!byId.has(id)) {
        // Fall back to full workspace list (not just showInSidebar) so tabs from
        // hidden or non-sidebar workspaces still get a labelled group header
        const resolvedWs = workspaces.find((w) => w.id === id) ?? null
        byId.set(id, {
          groupId: id,
          workspace: resolvedWs,
          tabs: [],
        })
      }
      byId.get(id)!.tabs.push(tab)
    }
    for (const g of byId.values()) {
      if (g.groupId === UNGROUPED_KEY) {
        g.tabs = sortAppGroupTabs(g.tabs)
      }
    }
    const result = Array.from(byId.values()).filter((g) => g.tabs.length > 0)
    result.sort((a, b) => {
      // App group (Home) is always the leftmost tab group
      if (a.groupId === UNGROUPED_KEY) return -1
      if (b.groupId === UNGROUPED_KEY) return 1
      if (groupOrder.length > 0) {
        const ai = groupOrder.indexOf(a.groupId)
        const bi = groupOrder.indexOf(b.groupId)
        // Known positions: sort by stored order
        if (ai !== -1 && bi !== -1) return ai - bi
        // Only one known: known first
        if (ai !== -1) return -1
        if (bi !== -1) return 1
      }
      return (a.workspace?.name ?? '').localeCompare(b.workspace?.name ?? '')
    })
    return result
  }, [visibleTabs, workspaces, activeWorkspaceIds, groupOrder])

  // Enter / exit motion for workspace groups (Only parks a group; restore brings it back).
  const [enteringGroupIds, setEnteringGroupIds] = useState<Set<string>>(() => new Set())
  const [exitingGroups, setExitingGroups] = useState<TabGroup[]>([])
  const prevWorkspaceGroupIdsRef = useRef<string[] | null>(null)
  const groupsByIdRef = useRef<Map<string, TabGroup>>(new Map())

  useEffect(() => {
    for (const g of groups) {
      if (g.groupId !== UNGROUPED_KEY) {
        groupsByIdRef.current.set(g.groupId, g)
      }
    }
  }, [groups])

  useEffect(() => {
    const currentIds = groups
      .map((g) => g.groupId)
      .filter((id) => id !== UNGROUPED_KEY)
    const prev = prevWorkspaceGroupIdsRef.current
    // First paint: record ids without animating every group in.
    if (prev === null) {
      prevWorkspaceGroupIdsRef.current = currentIds
      return
    }
    const added = currentIds.filter((id) => !prev.includes(id))
    const removed = prev.filter((id) => !currentIds.includes(id))
    const duration = tabGroupMotionDurationMs()

    if (added.length > 0) {
      setEnteringGroupIds((prevSet) => {
        const next = new Set(prevSet)
        for (const id of added) next.add(id)
        return next
      })
      if (duration > 0) {
        window.setTimeout(() => {
          setEnteringGroupIds((prevSet) => {
            const next = new Set(prevSet)
            for (const id of added) next.delete(id)
            return next
          })
        }, duration)
      } else {
        setEnteringGroupIds(new Set())
      }
    }

    if (removed.length > 0) {
      const exiting = removed
        .map((id) => groupsByIdRef.current.get(id))
        .filter((g): g is TabGroup => Boolean(g))
      if (exiting.length > 0) {
        setExitingGroups((prevExit) => {
          const ids = new Set(prevExit.map((g) => g.groupId))
          return [...prevExit, ...exiting.filter((g) => !ids.has(g.groupId))]
        })
        if (duration > 0) {
          window.setTimeout(() => {
            setExitingGroups((prevExit) =>
              prevExit.filter((g) => !removed.includes(g.groupId)),
            )
          }, duration)
        } else {
          setExitingGroups([])
        }
      }
    }

    prevWorkspaceGroupIdsRef.current = currentIds
  }, [groups])

  const displayGroups = useMemo(() => {
    if (exitingGroups.length === 0) return groups
    const liveIds = new Set(groups.map((g) => g.groupId))
    const extras = exitingGroups.filter((g) => !liveIds.has(g.groupId))
    if (extras.length === 0) return groups
    return [...groups, ...extras]
  }, [groups, exitingGroups])

  const flattenedDisplayOrder = useMemo(
    () => displayGroups.flatMap((g) => g.tabs.map((t) => t.id)),
    [displayGroups]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setActiveId(null)

    if (over?.id === "chat-input") {
      const tab = openTabs.find(t => t.id === active.id)
      if (tab && onTabDragToChat) {
        const displayTitle = tab.type === "explorer" ? getExplorerTabTitle(tab, workspacesRoute) : tab.title
        onTabDragToChat(tab.id, displayTitle, tab.type)
      }
      return
    }

    if (active.id !== over?.id && over) {
      const oldIndex = flattenedDisplayOrder.indexOf(active.id as string)
      const newIndex = flattenedDisplayOrder.indexOf(over.id as string)
      if (oldIndex === -1 || newIndex === -1) return

      // Determine which group each tab belongs to
      const getGroupId = (tabId: string) => {
        for (const g of groups) {
          if (g.tabs.some(t => t.id === tabId)) return g.groupId
        }
        return UNGROUPED_KEY
      }
      const activeGroupId = getGroupId(active.id as string)
      const overGroupId = getGroupId(over.id as string)

      // Cross-group drag involving UNGROUPED (inbox) tab
      if (activeGroupId !== overGroupId) {
        if (activeGroupId === UNGROUPED_KEY) {
          // Inbox tab dropped onto a workspace group — reorder groups, don't reorder tabs
          const currentGroupIds = groups.map(g => g.groupId)
          const fromIdx = currentGroupIds.indexOf(UNGROUPED_KEY)
          const toIdx = currentGroupIds.indexOf(overGroupId)
          if (fromIdx !== -1 && toIdx !== -1) {
            persistGroupOrder(arrayMove(currentGroupIds, fromIdx, toIdx))
          }
          return
        }
        // Workspace tab dropped onto UNGROUPED — block (inbox is not a workspace group)
        if (overGroupId === UNGROUPED_KEY) return
        // Two different workspace groups — allow flat reorder (tab stays in its workspace group on re-render)
      }

      const newOrderIds = arrayMove(flattenedDisplayOrder, oldIndex, newIndex)
      const reorderedTabs = pinHomeTabFirstInOpenTabs(
        newOrderIds
          .map((id) => openTabs.find((t) => t.id === id))
          .filter((t): t is OpenTab => t != null),
      )
      onTabReorder?.(reorderedTabs)
    }
  }

  const activeTab = activeId ? openTabs.find(tab => tab.id === activeId) : null
  const totalTabs = visibleTabs.length

  // Debounced scroll position check using requestAnimationFrame
  const rafRef = useRef<number>(0)
  const checkScrollPosition = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      if (!scrollContainerRef.current) return
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      const newLeft = scrollLeft > 0
      const newRight = scrollLeft < scrollWidth - clientWidth - 1
      setCanScrollLeft(prev => prev !== newLeft ? newLeft : prev)
      setCanScrollRight(prev => prev !== newRight ? newRight : prev)
    })
  }, [])

  // Auto-scroll to active tab using rAF (no nested timeouts)
  useEffect(() => {
    if (!scrollContainerRef.current || !activeTabId) return

    const frameId = requestAnimationFrame(() => {
      const container = scrollContainerRef.current
      if (!container) return

      const tabWrapper = container.querySelector(`[data-tab-id="${activeTabId}"]`) as HTMLElement
      if (!tabWrapper) return

      const activeTabElement = tabWrapper.querySelector('.group.flex.items-center') as HTMLElement
      if (!activeTabElement) return

      const containerRect = container.getBoundingClientRect()
      const tabRect = activeTabElement.getBoundingClientRect()

      const isVisible = tabRect.left >= containerRect.left && tabRect.right <= containerRect.right

      if (!isVisible) {
        activeTabElement.scrollIntoView({
          behavior: 'instant' as ScrollBehavior,
          block: 'nearest',
          inline: tabRect.left < containerRect.left ? 'start' : 'end'
        })
      }

      checkScrollPosition()
    })

    return () => cancelAnimationFrame(frameId)
  }, [activeTabId, checkScrollPosition])

  // Scroll and wheel listeners with passive-where-possible approach
  useEffect(() => {
    checkScrollPosition()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollPosition, { passive: true })
      window.addEventListener('resize', checkScrollPosition)

      // Only use non-passive for Shift+wheel horizontal scroll conversion
      const handleWheel = (e: WheelEvent) => {
        const canScrollHorizontally = container.scrollWidth > container.clientWidth
        if (!canScrollHorizontally) return

        if (e.shiftKey) {
          e.preventDefault()
          container.scrollLeft += e.deltaY
        } else if (Math.abs(e.deltaX) > 0) {
          // Native horizontal scroll (trackpad) - let it happen natively
          // but update scroll indicators
          checkScrollPosition()
        }
      }

      container.addEventListener('wheel', handleWheel, { passive: false })

      return () => {
        container.removeEventListener('scroll', checkScrollPosition)
        window.removeEventListener('resize', checkScrollPosition)
        container.removeEventListener('wheel', handleWheel)
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
      }
    }
  }, [openTabs.length, checkScrollPosition])

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }, [])

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }, [])

  const handleSidebarNativeDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!onNativeSidebarItemDrop) return
      // Only claim sidebar document/folder drags. OS file drops must bubble to
      // document-viewer text-import handlers (otherwise overlay sticks / drop dies).
      const types = Array.from(e.dataTransfer?.types ?? [])
      if (types.includes('Files') || !types.includes('application/json')) return
      e.preventDefault()
      e.stopPropagation()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    },
    [onNativeSidebarItemDrop]
  )

  const handleSidebarNativeDrop = useCallback(
    (e: React.DragEvent) => {
      if (!onNativeSidebarItemDrop) return
      // Do not steal OS file drops - leave them for text-import onDrop.
      if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) return
      const raw = e.dataTransfer.getData('application/json')
      const payload = parseSidebarDocumentDragJson(raw)
      if (!payload) return
      e.preventDefault()
      e.stopPropagation()
      onNativeSidebarItemDrop(payload)
    },
    [onNativeSidebarItemDrop]
  )

  if (!openTabs || openTabs.length === 0) return null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={flattenedDisplayOrder}
        strategy={horizontalListSortingStrategy}
      >
        <div
          className={cn(
            "document-tabs-bar relative flex w-full items-end overflow-hidden border-b border-border",
            SHELL_COLUMN_HEADER_CLASS,
            workspaceFocus && "document-tabs--workspace-focus",
          )}
          data-testid={testIds.documentViewer.tabList}
          style={{
            ...(workspaceFocus
              ? null
              : { backgroundColor: 'hsl(var(--tab-bar-bg))' }),
            ...(focusWorkspaceColor
              ? ({ '--ws-color': focusWorkspaceColor } as React.CSSProperties)
              : null),
          }}
          onDragOverCapture={onNativeSidebarItemDrop ? handleSidebarNativeDragOver : undefined}
          onDropCapture={onNativeSidebarItemDrop ? handleSidebarNativeDrop : undefined}
        >
          <div className="relative flex w-0 min-w-0 flex-1 items-end self-stretch">
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-0 bottom-0 z-20 px-1 bg-muted/30 hover:bg-muted/50 flex items-center transition-colors"
              title="Scroll left"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            id="document-tabs-scroll-container"
            className="flex-1 flex items-end gap-0.5 overflow-x-auto overflow-y-hidden scrollbar-hide scroll-smooth min-w-0 max-w-full"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              height: '100%',
              width: '100%'
            }}
          >
            {canScrollLeft && (
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-muted/30 to-transparent pointer-events-none z-10" />
            )}
            {canScrollRight && (
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-muted/30 to-transparent pointer-events-none z-10" />
            )}

            <div className="flex items-end gap-0.5 pl-1 pr-2 flex-nowrap shrink-0">
              {displayGroups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.groupId)
                const isAppGroup = group.groupId === UNGROUPED_KEY
                const isExiting = exitingGroups.some((g) => g.groupId === group.groupId)
                const isEntering = enteringGroupIds.has(group.groupId)
                const label = group.workspace ? group.workspace.name : 'App'
                const color = group.workspace?.color ?? null
                const sortedTabs = group.tabs
                const ghostPlacement = ghostImportTab?.placement ?? 'right'
                const ghostRelativeId = ghostImportTab?.relativeToTabId ?? activeTabId
                const ghostLabel = ghostImportTab?.label ?? 'New import'
                const renderTabWithGhost = (tab: OpenTab) => {
                  const isGhostAnchor =
                    Boolean(ghostImportTab) && tab.id === ghostRelativeId
                  return (
                    <div key={tab.id} className="contents">
                      {isGhostAnchor && ghostPlacement === 'left' ? (
                        <GhostImportTabChrome label={ghostLabel} placement="left" />
                      ) : null}
                      <div
                        data-tab-id={tab.id}
                        className={cn(
                          'tab-group__tab-wrap relative',
                          isGhostAnchor && ghostPlacement === 'replace' && 'z-[1]',
                        )}
                      >
                        <SortableTab
                          tab={tab}
                          isActive={activeTabId === tab.id}
                          onTabClick={onTabClick}
                          onTabClose={onTabClose}
                          onTabPinToggle={onTabPinToggle}
                          totalTabs={totalTabs}
                          onTabShowFileLocation={onTabShowFileLocation}
                          onTabRename={onTabRename}
                          onTabMove={onTabMove}
                          onTabExportPDF={onTabExportPDF}
                          onTabAddToChat={onTabAddToChat}
                          onTabCloseOthers={onTabCloseOthers}
                          onTabMoveToWindow={onTabMoveToWindow}
                          onTabCopyToWindow={onTabCopyToWindow}
                          getTabFilePath={getTabFilePath}
                          onTabToggleBookmark={onTabToggleBookmark}
                          getTabBookmarkStatus={getTabBookmarkStatus}
                          workspacesRoute={workspacesRoute}
                          workspaceGroupId={isAppGroup ? undefined : group.groupId}
                          groupCollapsed={isCollapsed}
                          onTabToggleGroupCollapsed={
                            isAppGroup ? undefined : toggleGroupCollapsed
                          }
                        />
                        {isGhostAnchor && ghostPlacement === 'replace' ? (
                          <GhostImportTabChrome label={ghostLabel} placement="replace" />
                        ) : null}
                      </div>
                      {isGhostAnchor && ghostPlacement === 'right' ? (
                        <GhostImportTabChrome label={ghostLabel} placement="right" />
                      ) : null}
                    </div>
                  )
                }

                if (isAppGroup) {
                  return sortedTabs.map((tab) => renderTabWithGhost(tab))
                }

                const groupHasActive = group.tabs.some((tab) => tab.id === activeTabId)
                return (
                  <div
                    key={group.groupId}
                    className={cn(
                      'tab-group flex items-end gap-0.5 flex-nowrap',
                      groupHasActive && 'tab-group--has-active',
                      isCollapsed && 'tab-group--collapsed',
                      isExiting && tabGroupExitClass(),
                      isEntering && tabGroupEnterClass(),
                    )}
                    style={color ? { '--ws-color': color } as React.CSSProperties : undefined}
                    onContextMenu={(e) => {
                      const target = e.target as HTMLElement
                      if (target.closest(".tab-group__tab")) return
                      openWsRowMenu(group.groupId, e)
                    }}
                  >
                    <TabGroupHeader
                      groupId={group.groupId}
                      label={label}
                      tabCount={group.tabs.length}
                      isCollapsed={isCollapsed}
                      onToggleCollapse={() => toggleGroupCollapsed(group.groupId)}
                      onContextMenu={(e) => openWsRowMenu(group.groupId, e)}
                    />
                    <TabGroupStrip
                      isCollapsed={isCollapsed}
                      tabCount={sortedTabs.length}
                    >
                      {sortedTabs.map((tab) => renderTabWithGhost(tab))}
                    </TabGroupStrip>
                  </div>
                )
              })}

            </div>
          </div>

          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-0 bottom-0 z-20 px-1 bg-muted/30 hover:bg-muted/50 flex items-center transition-colors"
              title="Scroll right"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          </div>

          {onNewTab ? (
            <div
              className="relative z-30 ml-auto flex shrink-0 items-end self-stretch border-l border-border/60 pl-1.5 pr-1.5"
              data-testid={testIds.documentTabs.newDocument}
            >
              <CreateInFolderDropdown
                variant="tab-bar"
                onPrimaryClick={onNewTab}
                onChooseFolder={onChooseNewDocumentFolder}
                documentLabel={`New document in ${newDocumentFolderLabel?.trim() || 'workspace'}`}
                primaryTestId={testIds.documentTabs.newDocumentPrimary}
                menuTestId={testIds.documentTabs.newDocumentMenu}
                chooseFolderTestId={testIds.documentTabs.newDocumentChooseFolder}
                itemTestIds={{
                  document: testIds.documentTabs.newDocumentInFolder,
                  import: testIds.documentTabs.newDocumentImport,
                  folder: testIds.documentTabs.newDocumentNewFolder,
                }}
                onAction={(id: CreateInFolderActionId) => {
                  if (id === 'document') onNewTab()
                  else if (id === 'import') onImportInFolder?.()
                  else onNewFolder?.()
                }}
              />
            </div>
          ) : null}
        </div>
      </SortableContext>

      <WorkspaceRowContextMenuPortal
        menu={wsRowMenu}
        menuRef={wsRowMenuRef}
        onClose={closeWsRowMenu}
        onToggleWorkspace={toggleWorkspace}
        onHideOtherWorkspaces={hideOtherWorkspaces}
        activeWorkspaceIds={activeWorkspaceIds}
        workspaceHiddenFromScope={
          wsRowMenu ? !activeWorkspaceIds.includes(wsRowMenu.wsId) : false
        }
        groupCollapse={
          wsRowMenu
            ? {
                collapsed: collapsedGroups.has(wsRowMenu.wsId),
                onToggle: () => toggleGroupCollapsed(wsRowMenu.wsId),
              }
            : null
        }
      />

      <DragOverlay>
        {activeTab ? (
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border shadow-lg text-sm font-medium text-foreground max-w-[200px]"
          >
            {getTabIcon(activeTab.type)}
            <span className="truncate text-xs">
              {activeTab.type === "explorer" ? getExplorerTabTitle(activeTab, workspacesRoute) : activeTab.title}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
